import React, { useMemo } from 'react';
import { graphql, usePreloadedQuery, useFragment } from 'react-relay';
import { useIntl } from '@atlassian/jira-intl';
import type {
	versionMenu_softwareBacklog_VersionMenuContent$key,
	versionMenu_softwareBacklog_VersionMenuContent$data,
} from '@atlassian/jira-relay/src/__generated__/versionMenu_softwareBacklog_VersionMenuContent.graphql';
import type { versionMenuQuery } from '@atlassian/jira-relay/src/__generated__/versionMenuQuery.graphql';
import { MenuNodeType } from '@atlassian/jira-software-context-menu/src/common/types.tsx';
import { useTextFilter } from '@atlassian/jira-software-context-menu/src/common/utils/use-text-filter/index.tsx';
import { ContextMenuSearch } from '@atlassian/jira-software-context-menu/src/ui/menu-renderer/context-menu-inner/context-menu-node/context-menu-search/index.tsx';
import { ContextMenuNode } from '@atlassian/jira-software-context-menu/src/ui/menu-renderer/context-menu-inner/context-menu-node/index.tsx';
import {
	A11yList,
	A11yListItem,
} from '@atlassian/jira-software-context-menu/src/ui/menu-renderer/context-menu-inner/index.tsx';
import { VERSION_MENU_ID } from '../../../constants.tsx';
import messages from '../../../messages.tsx';
import type { VersionMenuProps, VersionMenuContentProps } from './types.tsx';
import { UnlinkMenuItem } from './unlink-menu-item/index.tsx';
import { VersionMenuItem } from './version-menu-item/index.tsx';

const OptionType = {
	Version: 'version',
	Unlink: 'unlink',
} as const;

type OptionType = (typeof OptionType)[keyof typeof OptionType];
type Option =
	| {
			type: typeof OptionType.Version;
			edge: versionMenu_softwareBacklog_VersionMenuContent$data[0];
	  }
	| { type: typeof OptionType.Unlink; label: string };

const transformOptionToString = (option: Option) =>
	option.type === OptionType.Version ? option.edge.node.name : option.label;

export const VERSION_MENU_QUERY = graphql`
	query versionMenuQuery($jiraProjectIds: [ID!]!, $first: Int!, $after: String) {
		jira @required(action: THROW) @optIn(to: ["JiraVersionsForProjects"]) {
			versionsForProjects(
				jiraProjectIds: $jiraProjectIds
				first: $first
				after: $after
				filter: [RELEASED, UNRELEASED]
			) @required(action: THROW) @connection(key: "versionBacklogPanelRelay_versionsForProjects") {
				edges @required(action: THROW) {
					...versionMenu_softwareBacklog_VersionMenuContent
				}
			}
		}
	}
`;

export const VersionMenu = ({ versionMenuQueryRef, ...props }: VersionMenuProps) => {
	const data = usePreloadedQuery<versionMenuQuery>(VERSION_MENU_QUERY, versionMenuQueryRef);
	const versionEdges = useMemo(() => data.jira.versionsForProjects.edges.filter(Boolean), [data]);

	return <VersionMenuContent {...props} versionEdges={versionEdges} />;
};

const VersionMenuContent = ({
	selectedCardIds,
	selectedCards,
	menuItemNodeArgs,
	versionEdges,
}: VersionMenuContentProps) => {
	const { formatMessage } = useIntl();

	// Without breaking the edges between query and fragment, we cannot make the fields here required due to the connection
	const versions = useFragment<versionMenu_softwareBacklog_VersionMenuContent$key>(
		graphql`
			fragment versionMenu_softwareBacklog_VersionMenuContent on JiraVersionEdge
			@relay(plural: true) {
				node @required(action: THROW) {
					versionId @required(action: THROW)
					name @required(action: THROW)
					status @required(action: THROW)
					project @required(action: THROW) {
						projectId @required(action: THROW)
					}
				}
				...versionMenuItem_softwareBacklog
			}
		`,
		versionEdges,
	);

	const versionOptions: Option[] = useMemo(() => {
		// Menu's only rendered if selected cards belong to the same project, so we can filter the versions using it
		const projectId = String(selectedCards[0].projectId);
		const filteredVersions = versions.filter(
			({ node }) => node.status === 'UNRELEASED' && node.project.projectId === projectId,
		);

		// If there's only 1 card selected, ensure any already assigned versions are removed from the list
		const firstCardVersionIds = selectedCards[0]?.fixVersions ?? [];
		const availableVersions =
			selectedCards.length === 1 && firstCardVersionIds.length > 0
				? filteredVersions.filter(({ node }) => !firstCardVersionIds.includes(node.versionId))
				: filteredVersions;

		return availableVersions.map((edge) => ({ type: OptionType.Version, edge }));
	}, [versions, selectedCards]);

	// Show unlink option if any card's version can be found in our queried versions
	const versionIdsSet = useMemo(
		() => new Set(versions.map(({ node }) => node.versionId)),
		[versions],
	);
	const showUnlinkMenuItem = useMemo(
		() =>
			selectedCards.some(
				(card) => card.fixVersions?.some((versionId) => versionIdsSet.has(versionId)) ?? false,
			),
		[selectedCards, versionIdsSet],
	);

	const options: Option[] = useMemo(
		() =>
			showUnlinkMenuItem
				? [
						{
							type: OptionType.Unlink,
							label: formatMessage(messages.versionUnlinkOptionLabel),
						},
						...versionOptions,
					]
				: versionOptions,
		[versionOptions, formatMessage, showUnlinkMenuItem],
	);
	const [filteredOptions, updateFilter] = useTextFilter<Option>(options, transformOptionToString);

	return versionOptions.length > 0 ? (
		<ContextMenuNode
			{...menuItemNodeArgs}
			menuNode={{
				type: MenuNodeType.Item,
				id: `${VERSION_MENU_ID}/menu`,
				label: formatMessage(messages.versionOptionLabel),
				submenuWidth: 284,
				element: (
					<ContextMenuSearch
						onChange={updateFilter}
						autoFocus
						isAsyncSearch={false}
						isLoading={false}
						hasSearch={options.length > 6}
						hasResult={filteredOptions.length > 0}
						resultsCount={filteredOptions.length}
					>
						<A11yList>
							{filteredOptions.map((option) =>
								option.type === OptionType.Version ? (
									<A11yListItem key={option.edge.node.versionId}>
										<VersionMenuItem
											selectedCardIds={selectedCardIds}
											versionEdge={option.edge}
											menuItemNodeArgs={menuItemNodeArgs}
										/>
									</A11yListItem>
								) : (
									<A11yListItem key="unlink">
										<UnlinkMenuItem
											key="unlink"
											selectedCardIds={selectedCardIds}
											label={option.label}
											menuItemNodeArgs={menuItemNodeArgs}
										/>
									</A11yListItem>
								),
							)}
						</A11yList>
					</ContextMenuSearch>
				),
			}}
		/>
	) : null;
};
